import React, { useEffect, useState } from "react";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import styled from "@emotion/styled";
import {
  Alert as MuiAlert,
  Box,
  MenuItem,
  TextField as MuiTextField,
  ListItemText,
  Button,
  Stack,
  Paper,
  Divider as MuiDivider,
  IconButton,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CircularProgress from "@mui/material/CircularProgress";
import CancelIcon from "@mui/icons-material/Cancel";
import { Helmet } from "react-helmet-async";
import { spacing } from "@mui/system";
import useNotification from "hooks/useNotification";
import useReferentiel from "hooks/useReferentiel";
import UploadSingleFile from "components/file/UploadSingleFile";
import ImgBoxFromUrl from "components/media/ImgBoxFromUrl";
import AudioBoxFromUrl from "components/media/AudioBoxFromUrl";
import VideoBoxFromUrl from "components/media/VideoBoxFromUrl";
import { API_URL } from "config";
import useChallenge from "hooks/useChallenge";
import useProgram from "hooks/useProgram";
import BackBtn from "components/custom/BackBtn";
import AlertMsg from "components/custom/AlertMsg";
import { getMediaUrl } from "utils/objectUtil";
import EditFormSubtitle from "components/media/EditFormSubtitle";
import PointgainedEditBtn from "./PointgainedEditBtn";
import PointgainedInfo from "./PointgainedInfo";
import DeleteMediaBtn from "./DeleteMediaBtn";
// ----------------------------------------------------------------------
const REF_MODEL = "challenge";
const AUDIO_TYPE = "63945a9991e8970b002dbf03";
const NOTE_TYPE = "63945aa491e8970b002dbf0b";
const VIDEO_TYPE = "63945aac91e8970b002dbf13";
const Divider = styled(MuiDivider)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Item = styled(Paper)(({ theme }) => ({
  //backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));
export default function ChallengeEditPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { boosters, getBoosters } = useProgram();
  useEffect(() => {
    getBoosters();
  }, []);
  const {
    findChallengeById,
    getChallenges,
    saveChallenge,
    saveChallengeFormData,
  } = useChallenge();
  const { notif } = useNotification();
  const { refs } = useReferentiel();
  const [editData, seteditdata] = useState(null);
  const [editPoints, seteditPoints] = useState([]);
  useEffect(() => {
    if (!id || id === "add") return;
    init();
  }, [id]);
  const init = async () => {
    try {
      const res = await findChallengeById(id);
      if (res) {
        seteditdata(res);
      }
    } catch (error) {
      console.error();
    }
  };
  useEffect(() => {
    if (!editData) return;
    if (!editData.pointgained) return;
    seteditPoints(editData.pointgained);
  }, [editData]);

  const dataSchema = Yup.object().shape({
    code: Yup.string().max(20).required("Veuillez renseigner le code"),
    title: Yup.string().max(100).required(`Veuillez renseigner le titre`),
    refsubject: Yup.string().max(100).required(`Veuillez renseigner le sujet`),
    inspirenote: Yup.string(),
    predefinedNote: Yup.string(),
    booster: Yup.string(),
    refchallengetype: Yup.string()
      .max(100)
      .required(`Veuillez renseigner le type de défi`),
    refstereotype: Yup.string()
      .max(100)
      .required(`Veuillez renseigner le stereotype`),
    image:
      editData && editData.id
        ? Yup.string()
        : Yup.mixed().required("Veuillez renseigner l'image"),
    inspirecontent:
      editData && editData.id
        ? Yup.string()
        : Yup.mixed().required("Veuillez renseigner la vidéo d'inspiration"),

    pointgained: Yup.array()
      .required("Veuillez renseigner les paramètres des points")
      .min(1, "Les paramètres des points ne sont pas valides")
      .max(50, "Les paramètres des points ne sont pas valides"),
  });
  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      code: editData && editData.code ? editData.code : "",
      title: editData && editData.title ? editData.title : "",
      inspirenote: editData && editData.inspirenote ? editData.inspirenote : "",
      predefinedNote:
        editData && editData.predefinedNote ? editData.predefinedNote : "",
      refsubject: editData && editData.refsubject ? editData.refsubject.id : "",
      booster: editData && editData.booster ? editData.booster.id : "",
      refchallengetype:
        editData && editData.refchallengetype
          ? editData.refchallengetype.id
          : "",
      refstereotype:
        editData && editData.refstereotype ? editData.refstereotype.id : "",
      pointgained: "",
    },
    validationSchema: dataSchema,
    onSubmit: async () => {
      try {
        const newData =
          editData && editData.id ? { ...editData, ...values } : { ...values };
        /* in case file upload is choiced we need to make formdata */
        const isFormdata =
          values.image ||
          values.bgimage ||
          values.femalevoice ||
          values.malevoice ||
          values.inspirecontent;
        if (isFormdata) {
          const formData = new FormData();
          delete newData.modifiedby;
          delete newData.createdby;
          if (values.image) {
            delete newData.image;
            formData.append("image", values.image);
          }
          if (values.bgimage) {
            delete newData.bgimage;
            formData.append("bgimage", values.bgimage);
          }
          if (values.femalevoice) {
            delete newData.femalevoice;
            formData.append("femalevoice", values.femalevoice);
          }
          if (values.malevoice) {
            delete newData.malevoice;
            formData.append("malevoice", values.malevoice);
          }
          if (
            values.inspirecontent &&
            String(values.refchallengetype) !== "63945aa491e8970b002dbf0b"
          ) {
            delete newData.inspirecontent;
            formData.append("inspirecontent", values.inspirecontent);
          }
          Object.entries(newData).forEach(([e, k]) => {
            if (e === "pointgained") {
              formData.append(e, JSON.stringify(k));
            } else {
              formData.append(e, k);
            }
          });

          await saveChallengeFormData(newData, formData, REF_MODEL);
        } else {
          await saveChallenge(
            {
              ...newData,
            },
            REF_MODEL
          );
        }
        await getChallenges();
        notif("Sauvegarde réussie");
        navigate("/app/prog-challenge");
      } catch (error) {
        console.error(error);
        notif(null, error);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setSubmitting,
    handleChange,
    setFieldValue,
  } = formik;

  useEffect(() => {
    if (!setFieldValue) return;
    if (!editPoints) return;
    setFieldValue("pointgained", editPoints);
  }, [editPoints, setFieldValue]);

  if (!refs) return;
  const { refsubject, refchallengetype, refstereotype } = refs;
  const handleCompletePoint = (data) => {
    try {
      seteditPoints(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelPoint = (data) => {
    try {
      const newPoints = [...editPoints].filter((it) => it.id !== data.id);
      seteditPoints(newPoints);
    } catch (error) {
      console.error(error);
    }
  };
  if (!refsubject) return <></>;
  return (
    <>
      <Helmet title="Edition Stereotype programme" />
      <BackBtn
        backUrl="/app/prog-challenge"
        needConfirm={true}
        titleConfim="Demande de confirmation"
        msgConfirm="Des données saisies pourraient ne pas être enregistrées.. Voulez-vous quitter cette page ?"
      />
      <Divider my={6} />
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <AlertMsg errors={errors} />
          <TextField
            fullWidth
            type="text"
            label="Code"
            {...getFieldProps("code")}
            error={Boolean(touched.code && errors.code)}
            helperText={touched.code && errors.code}
            my={2}
          />
          <TextField
            fullWidth
            type="text"
            name="title"
            label="Titre"
            {...getFieldProps("title")}
            error={Boolean(touched.title && errors.title)}
            helperText={touched.title && errors.title}
            my={2}
          />
          <TextField
            fullWidth
            select
            label="Type de défi"
            value={values.refchallengetype}
            onChange={handleChange("refchallengetype")}
            {...getFieldProps("refchallengetype")}
            error={Boolean(touched.refchallengetype && errors.refchallengetype)}
            helperText={touched.refchallengetype && errors.refchallengetype}
            my={2}
          >
            {refchallengetype.map((option, i) => (
              <MenuItem key={i} value={option.id}>
                {`${option.label}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label="Sujet"
            value={values.refsubject}
            onChange={handleChange("refsubject")}
            {...getFieldProps("refsubject")}
            error={Boolean(touched.refsubject && errors.refsubject)}
            helperText={touched.refsubject && errors.refsubject}
            my={2}
          >
            {refsubject.map((option, i) => (
              <MenuItem key={i} value={option.id}>
                {`${option.code}-${option.label}`}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ my: 2 }} display="flex" alignItems="center">
            <TextField
              fullWidth
              select
              label="Booster"
              value={values.booster}
              onChange={handleChange("booster")}
              {...getFieldProps("booster")}
              error={Boolean(touched.booster && errors.booster)}
              helperText={touched.booster && errors.booster}
            >
              {boosters.map((option, i) => (
                <MenuItem key={i} value={option.id}>
                  {`${option.refboostertype.label}-${option.label}`}
                </MenuItem>
              ))}
            </TextField>
            <IconButton
              sx={{ ml: 3 }}
              onClick={() => setFieldValue("booster", "")}
            >
              <CancelIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            select
            label="Stereotype du défi"
            value={values.refstereotype}
            onChange={handleChange("refstereotype")}
            {...getFieldProps("refstereotype")}
            error={Boolean(touched.refstereotype && errors.refstereotype)}
            helperText={touched.refstereotype && errors.refstereotype}
            my={2}
          >
            {refstereotype.map((option, i) => (
              <MenuItem key={i} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Stack
            alignItems="center"
            justifyContent="flex-start"
            sx={{ my: 6 }}
            direction="row"
            spacing={5}
          >
            {editData && editData.image && (
              <Item>
                <ImgBoxFromUrl
                  image={getMediaUrl(editData, "image", REF_MODEL)}
                  sx={{
                    borderRadius: 1,
                    objectFit: "cover",
                    width: "150px",
                    height: "auto",
                  }}
                />
              </Item>
            )}
            <Item>
              <UploadSingleFile
                value={values.image}
                label="Image d'illustration"
                onChange={(val) => setFieldValue("image", val)}
                caption="(Les formats *.jpeg and *.png sont acceptés)"
                error={Boolean(touched.image && errors.image)}
                sx={{ maxHeight: "150px" }}
                acceptFile={{
                  accept: {
                    "image/jpeg": [".jpeg", ".png"],
                  },
                }}
                mediaType="image"
              />
            </Item>
          </Stack>
          <Stack
            alignItems="center"
            justifyContent="flex-start"
            sx={{ my: 6 }}
            direction="row"
            spacing={5}
          >
            {editData && editData.bgimage && (
              <Item>
                <ImgBoxFromUrl
                  image={getMediaUrl(editData, "bgimage", REF_MODEL)}
                  sx={{
                    borderRadius: 1,
                    objectFit: "cover",
                    width: "150px",
                    height: "auto",
                  }}
                />
              </Item>
            )}
            <Item>
              <UploadSingleFile
                value={values.bgimage}
                label="Image de fond"
                onChange={(val) => setFieldValue("bgimage", val)}
                caption="(Les formats *.jpeg and *.png sont acceptés)"
                error={Boolean(touched.bgimage && errors.bgimage)}
                sx={{ maxHeight: "150px" }}
                acceptFile={{
                  accept: {
                    "image/jpeg": [".jpeg", ".png"],
                  },
                }}
                mediaType="image"
              />
            </Item>
          </Stack>
          <Stack
            alignItems="center"
            justifyContent="flex-start"
            sx={{ my: 6 }}
            direction="row"
            spacing={5}
          >
            {editData && editData.femalevoice && (
              <Item>
                <AudioBoxFromUrl
                  audio={getMediaUrl(editData, "femalevoice", REF_MODEL)}
                />
                {editData.femaletranscripts && (
                  <EditFormSubtitle
                    transcripts={editData.femaletranscripts}
                    model={REF_MODEL}
                    field="femaletranscripts"
                    idModel={editData.id}
                    onComplete={init}
                  />
                )}
              </Item>
            )}
            <Item>
              <UploadSingleFile
                value={values.femalevoice}
                label="Voix féminine"
                onChange={(val) => setFieldValue("femalevoice", val)}
                caption="(Les formats *.mp3 sont acceptés)"
                error={Boolean(touched.femalevoice && errors.femalevoice)}
                sx={{ maxHeight: "150px" }}
                acceptFile={{
                  accept: {
                    "audio/mp3": [".mp3"],
                  },
                }}
                mediaType="audio"
              />
            </Item>
          </Stack>
          <Stack
            alignItems="center"
            justifyContent="flex-start"
            sx={{ my: 6 }}
            direction="row"
            spacing={5}
          >
            {editData && editData.malevoice && (
              <Item>
                <AudioBoxFromUrl
                  audio={getMediaUrl(editData, "malevoice", REF_MODEL)}
                />
                {editData.maletranscripts && (
                  <EditFormSubtitle
                    transcripts={editData.maletranscripts}
                    model={REF_MODEL}
                    field="maletranscripts"
                    idModel={editData.id}
                    onComplete={init}
                  />
                )}
              </Item>
            )}
            <Item>
              <UploadSingleFile
                value={values.malevoice}
                label="Voix masculine"
                onChange={(val) => setFieldValue("malevoice", val)}
                caption="(Les formats *.mp3 sont acceptés)"
                error={Boolean(touched.malevoice && errors.malevoice)}
                sx={{ maxHeight: "150px" }}
                acceptFile={{
                  accept: {
                    "audio/mp3": [".mp3"],
                  },
                }}
                mediaType="audio"
              />
            </Item>
          </Stack>
          {values &&
          values.refchallengetype &&
          String(values.refchallengetype) === "63945aa491e8970b002dbf0b" ? (
            <Stack>
              <Item>
                <TextField
                  fullWidth
                  type="text"
                  multiline
                  rows={4}
                  maxRows={8}
                  name="predefinedNote"
                  label={`“Prédéfinie” : Note`}
                  {...getFieldProps("predefinedNote")}
                  error={Boolean(
                    touched.predefinedNote && errors.predefinedNote
                  )}
                  helperText={touched.predefinedNote && errors.predefinedNote}
                  my={2}
                />
              </Item>
              <Item>
                <TextField
                  fullWidth
                  type="text"
                  multiline
                  rows={4}
                  maxRows={8}
                  name="inspirenote"
                  label={`“Inspire moi” : Note`}
                  {...getFieldProps("inspirenote")}
                  error={Boolean(touched.inspirenote && errors.inspirenote)}
                  helperText={touched.inspirenote && errors.inspirenote}
                  my={2}
                />
              </Item>
            </Stack>
          ) : (
            <Stack
              alignItems="center"
              justifyContent="flex-start"
              sx={{ my: 6 }}
              direction="row"
              spacing={5}
            >
              {editData && editData.inspirecontent && (
                <Item>
                  <VideoBoxFromUrl
                    video={getMediaUrl(editData, "inspirecontent", "challenge")}
                  />
                  <Box sx={{ my: 4 }}>
                    <DeleteMediaBtn
                      editData={editData}
                      file={{
                        field: "inspirecontent",
                        path: editData.inspirecontent,
                      }}
                      refName={REF_MODEL}
                      onComplete={init}
                    />
                  </Box>
                </Item>
              )}

              <Item>
                <UploadSingleFile
                  value={values.inspirecontent}
                  label="Vidéo d'inspiration"
                  onChange={(val) => setFieldValue("inspirecontent", val)}
                  caption="(Les formats *.mp4 sont acceptés)"
                  error={Boolean(touched.malevoice && errors.malevoice)}
                  sx={{ maxHeight: "150px" }}
                  acceptFile={{
                    accept: {
                      "video/mp4": [".mp4"],
                    },
                  }}
                  mediaType="video"
                />
              </Item>
            </Stack>
          )}

          <Divider my={3} />
          <PointgainedEditBtn
            addLabel={`${
              editPoints && editPoints.length > 1 ? "Modifier" : "Ajouter"
            } les paramètres du défi par stereotype`}
            editPoints={editPoints}
            handleCompletePoint={handleCompletePoint}
            handleDelPoint={handleDelPoint}
          />
          {editPoints && <PointgainedInfo editPoints={editPoints} />}
          <Divider my={6} />
          <Button
            sx={{ my: 2 }}
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            endIcon={
              isSubmitting ? (
                <CircularProgress size={20} />
              ) : (
                <DoneIcon size={20} />
              )
            }
          >
            Valider
          </Button>
        </Form>
      </FormikProvider>
    </>
  );
}
